import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, fromEvent, interval, merge, mergeMap, Observable, Subscription } from 'rxjs';
import { map, startWith, takeWhile, throttleTime } from 'rxjs/operators';

import { SessionDialogComponent } from '../components/session-dialog/session-dialog.component';

import { LoggerService } from './logger.service';

import { environment } from 'projects/admin/src/environments/environment';
import { revokeAuthentication } from 'shared/auth/store/auth.actions';
import { IAuthStore } from 'shared/auth/store/auth.reducer';
import { ExpirationCookieName } from 'shared/openapi/models/expiration-cookie-name';

@Injectable({
	providedIn: 'root',
})
export abstract class AuthBaseService {
	public roles$!: Observable<string[] | undefined>;
	public username$!: Observable<string>;
	public userId$!: Observable<string>;
	public name$!: Observable<string>;
	public authenticated$ = new BehaviorSubject(undefined);

	private readonly SESSION_CHECK_PERIOD_IN_MS: number = environment.keycloak.sessionCheckPeriodInMs;
	private readonly SESSION_EXPIRY_OFFSET: number = environment.keycloak.sessionExpiryOffset;

	private mouseInteraction$: Observable<Date>;
	private keyDownInteraction$: Observable<Date>;
	private clickInteraction$: Observable<Date>;
	private lastInteraction$: BehaviorSubject<Date> = new BehaviorSubject(new Date());

	private periodLength = 0;
	private checkSessionSub!: Subscription;

	protected constructor(
		protected logger: LoggerService,
		protected dialog: MatDialog,
		protected router: Router,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		protected store: Store<{ auth: IAuthStore }>
	) {
		this.mouseInteraction$ = fromEvent(document, 'mousemove').pipe(
			map(() => new Date()),
			startWith(new Date())
		);
		this.keyDownInteraction$ = fromEvent(document, 'keydown').pipe(
			map(() => new Date()),
			startWith(new Date())
		);
		this.clickInteraction$ = fromEvent(document, 'click').pipe(
			map(() => new Date()),
			startWith(new Date())
		);
		merge(this.mouseInteraction$, this.keyDownInteraction$, this.clickInteraction$).subscribe(
			(lastInteraction: Date) => {
				this.lastInteraction$.next(lastInteraction);
			}
		);
		this.setRole();
		this.setUsername();
		this.setAuthenticated();
		this.setUserId();
		this.setName();
	}

	public abstract logout(): void;

	public abstract keepSessionAlive(): void;

	public abstract preload(): Promise<void>;

	protected abstract handleSessionOnInit(expirationKey: string): Promise<void>;

	protected abstract onNotAuthenticated(): void;

	protected abstract getSessionExpirationTime(expirationKey: string): number;

	public async init(expirationKey: ExpirationCookieName): Promise<void> {
		await this.handleSessionOnInit(expirationKey);
		this.checkSession(expirationKey);
	}

	protected checkSession(cookieName: ExpirationCookieName) {
		this.authenticated$.subscribe((authenticated) => {
			this.checkSessionSub?.unsubscribe();
			if (authenticated) {
				this.checkSessionSub = interval(this.SESSION_CHECK_PERIOD_IN_MS)
					.pipe(
						mergeMap(() => this.authenticated$),
						takeWhile((bool) => bool === true),
						mergeMap(() => this.lastInteraction$),
						throttleTime(this.SESSION_CHECK_PERIOD_IN_MS - 100),
						map((lastInteraction) => {
							this.store
								.select('auth')
								.subscribe((data) => (this.periodLength = (data.sessionPeriod ?? 0) * 1000));
							this.checkRemainingTimeUntilExpiration(cookieName, lastInteraction);
						})
					)
					.subscribe();
			} else {
				this.onNotAuthenticated();
			}
		});
	}

	private checkRemainingTimeUntilExpiration(expirationKey: string, lastInteraction: Date) {
		if (this.getRemainingTimeUntilExpiration(expirationKey) <= 0) {
			this.checkSessionSub?.unsubscribe();
			this.store.dispatch(revokeAuthentication());
			this.dialog.closeAll();
			this.router.navigate(['/login']);
		} else if (this.getRemainingTimeUntilExpiration(expirationKey) <= this.SESSION_EXPIRY_OFFSET) {
			this.checkTheUserActivityInTheLastPeriod(expirationKey, lastInteraction);
		}
	}

	private checkTheUserActivityInTheLastPeriod(expirationKey: string, lastInteraction: Date) {
		if (this.wasTheUserActiveInTheLastPeriod(expirationKey, lastInteraction)) {
			this.keepSessionAlive();
		} else {
			this.dialog.closeAll();
			this.dialog.open(SessionDialogComponent);
		}
	}

	private getRemainingTimeUntilExpiration(expirationKey: string): number {
		const currentTime = new Date().getTime();
		const cookieExpirationTime = this.getSessionExpirationTime(expirationKey);
		return cookieExpirationTime - currentTime;
	}

	private wasTheUserActiveInTheLastPeriod(expirationKey: string, lastInteraction: Date): boolean {
		if (this.periodLength) {
			const periodEnd = this.getSessionExpirationTime(expirationKey);
			const periodStart = this.getSessionExpirationTime(expirationKey) - this.periodLength;
			return periodStart <= lastInteraction.getTime() && lastInteraction.getTime() <= periodEnd;
		}
		return false;
	}

	private setRole(): void {
		this.roles$ = this.store.pipe(
			select('auth'),
			map((authStore) => authStore.roles)
		);
	}

	private setUsername(): void {
		this.username$ = this.store.pipe(
			select('auth'),
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			map((authStore: any) => {
				if (authStore.name) {
					return authStore.name;
				} else {
					return '';
				}
			})
		);
	}

	private setUserId(): void {
		this.userId$ = this.store.pipe(
			select('auth'),
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			map((authStore: any) => {
				if (authStore.userId) {
					return authStore.userId;
				} else {
					return '';
				}
			})
		);
	}

	private setName(): void {
		this.name$ = this.store.pipe(
			select('auth'),
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			map((authStore: any) => {
				if (authStore.name) {
					return authStore.name;
				} else {
					return '';
				}
			})
		);
	}

	private setAuthenticated(): void {
		this.store
			.pipe(
				select('auth'),
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				map((authStore: any) => authStore.authenticated)
			)
			.subscribe((value) => {
				if (this.authenticated$.getValue() !== value) {
					this.authenticated$.next(value);
				}
			});
	}
}
